import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithQuery } from 'jotai-tanstack-query';

import { contextNavAtom, isAdvancingAtom, prevContextAtom } from '@/_atoms/navigation';
import { answerMapAtom } from '@/_atoms/answer';
import { respondentAtom, variantSelectionsAtom } from '@/_atoms/respondent';
import { personalizedSurveyGetQuery, surveyGetQuery } from '@/_queries/surveyQueries';
import {
  applyRandomSeedToSurvey,
  createElementContextMap,
  createFlatMapOfElements,
  createFlatMapOfStepIds,
  createStepMap,
  getStepIdAtIndex,
  groupUnansweredItemsAtEndOfSteps,
  removeLoginCodeEmailRequestFieldsFromSurvey
} from '@/_helpers/surveyHelpers';

import { currentMaterialAtom, materialsAtom } from '@/_atoms/batch';
import { Answer, Respondent, VariantSelection } from '@/types/respondent';
import type { Element, ElementContext, Survey, SurveyView, OptionSet, Step } from '@/types/survey';
import { ContentSelectionStrategy, Material, Variant} from '@/types/batch';

/** Whether to administer the variant for the given material. */
export const isVariantSelectedAtomFamily = atomFamily((materialId: string | null) =>
  atom<boolean>((get): boolean => {
    if (!materialId) return false;
    const materials: Material[] = get(materialsAtom);
    const material: Material | null = materials.find((m) => m.id === materialId) ?? null;

    if (!material?.variant) return false;

    const variantSelections: VariantSelection[] = get(variantSelectionsAtom);
    const variantSelection: VariantSelection | undefined = variantSelections.find(
      (selection) => selection.variant === material.variant!.id
    );
    return variantSelection?.isSelected ?? material.variant.isDefault;
  })
);

/**
 * The id of the currently active survey, derived from the current material and any relevant VariantSelection.
 *
 * Returns a string-formatted UUID, or null when there is no active survey.
 */
export const surveyIdAtom = atom<string | null>((get): string | null => {
  const currentMaterial: Material | null = get(currentMaterialAtom);
  if (!currentMaterial) return null;

  const variant: Variant | null = currentMaterial.variant;
  const isVariantSelected: boolean = get(isVariantSelectedAtomFamily(currentMaterial.id));

  return isVariantSelected && variant ? variant.survey : currentMaterial.survey;
});

/** The content selection strategy for the currently active material or its selected variant. */
export const contentSelectionStrategyAtom = atom<ContentSelectionStrategy | null>((get) => {
  const currentMaterial: Material | null = get(currentMaterialAtom);
  if (!currentMaterial) return null;

  const variant: Variant | null = currentMaterial.variant;
  const isVariantSelected: boolean = get(isVariantSelectedAtomFamily(currentMaterial.id));

  return isVariantSelected && variant ? variant.contentSelectionStrategy : currentMaterial.contentSelectionStrategy;
});

/**
 * Fetches and materializes a compact "survey view" for the active survey ID.
 * When there is an active survey (or a single survey in the batch), this atom:
 *  - fetches the survey,
 *  - applies respondent-specific transforms (login-code removal, randomization of item orders),
 *  - builds a compact view used by the UI (elements, element contexts, option sets, step flags).
 */
export const surveyViewAtom = atomWithQuery<SurveyView | null>((get) => {
  const surveyId: string | null = get(surveyIdAtom);
  const contentSelectionStrategy: ContentSelectionStrategy | null = get(contentSelectionStrategyAtom);
  const respondent: Respondent = get(respondentAtom);
  const answerMap: Map<string, Answer[]> = get(answerMapAtom);

  return {
    queryKey: ['survey-view', surveyId, contentSelectionStrategy, respondent?.id],
    queryFn: async () => {
      if (!surveyId || !respondent?.id) return null;

      const response = contentSelectionStrategy === null
        ? await surveyGetQuery(surveyId)
        : await personalizedSurveyGetQuery(contentSelectionStrategy, respondent.id, surveyId);
      const raw: Survey = response.data;
      const cleanedSurvey: Survey = respondent.hasSavedLoginCode ? removeLoginCodeEmailRequestFieldsFromSurvey(raw) : raw;
      const seededSurvey: Survey = applyRandomSeedToSurvey(respondent.randomSeed, cleanedSurvey);
      const survey: Survey = groupUnansweredItemsAtEndOfSteps(seededSurvey, answerMap);
      const elements: Element[] = createFlatMapOfElements(survey.steps);
      const flatMapOfStepIds: string[] = createFlatMapOfStepIds(survey.steps);

      return {
        id: surveyId,
        elements: elements,
        contextMap: createElementContextMap(elements, flatMapOfStepIds),
        hasProgressBarLabels: survey.steps.some((step: Step) => Boolean(step.progressBar?.label)),
        optionSetMap: new Map(survey.optionSets.map((o: OptionSet) => [o.id, o])),
        stepMap: createStepMap(survey.steps),
        firstStepId: getStepIdAtIndex(survey, 0),
        lastStepId: getStepIdAtIndex(survey, -1),
      } as SurveyView;
    },
    enabled: !!surveyId && !!respondent?.id,
    staleTime: Infinity, // never refetch unless manually
    gcTime: Infinity,    // never garbage collect
  };
});

/**
 * Elements on the current page, in render order.
 * Uses the current element's pageStart/pageEnd to slice the global `elements` array.
 */
export const pageElementsAtom = atom<Element[]>((get) => {
  const elements: Element[] = get(surveyElementsAtom);
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;

  if (!currentContext || elements.length == 0) return [];

  return elements.slice(currentContext.pageStart, currentContext.pageEnd + 1);
});

/**
 * Elements on the previous page, in render order.
 * Uses the last element on the previous page's pageStart/pageEnd to slice the global `elements` array.
 */
export const previousPageElementsAtom = atom<Element[]>((get) => {
  const elements: Element[] = get(surveyElementsAtom);
  const prevContext: ElementContext | null = get(prevContextAtom);

  if (!prevContext || elements.length == 0) return [];

  return elements.slice(prevContext.pageStart, prevContext.pageEnd + 1);
});

/**
 * Elements in the current survey, in render order.
 */
export const surveyElementsAtom = atom<Element[]>((get) => {
  return get(surveyViewAtom).data?.elements ?? [];
});

// Resolves to `SurveyView | null` and truly returns a Promise.
// TS is happy because we await a Promise-like value.
export const waitForSurveyViewAtom = atom<Promise<SurveyView | null>>(async (get) => {
  while (true) {
    const surveyId: string | null = get(surveyIdAtom);
    const respondent: Respondent = get(respondentAtom);
    const { data, error } = get(surveyViewAtom); // query result (sync object)

    // If the query errored or is missing required preconditions, return null.
    if (!surveyId || !respondent?.id || error) return null;

    // If the query is enabled and has data, return it
    if (data) return data as SurveyView;

    // Yield to the event loop so Jotai/query can progress
    await new Promise((r) => setTimeout(r, 20));
  }
});

/**
 * Create a derived atom that retrieves an option set for a given option set ID.
 *
 * @param optionSetId - The ID of the OptionSet to retrieve.
 * @returns A derived async atom that resolves to an OptionSet or null.
 * @throws Error if the specified OptionSet is not found.
 */
export const optionSetAtomFamily = atomFamily((optionSetId: string | null) =>
  atom<OptionSet | null | undefined>((get) => {
    if (!optionSetId) return null;
    const surveyView: SurveyView | null | undefined = get(surveyViewAtom)?.data;

    // When the survey view isn't yet loaded, return null.
    if (!surveyView) return null;

    const optionSet: OptionSet | undefined = surveyView.optionSetMap.get(optionSetId);

    if (!optionSet) {
      console.error(`Failed to find OptionSet with ID "${optionSetId}".`);
      throw new Error('Unable to load OptionSet for the item. Please contact the survey administrator.');
    }

    return optionSet;
  })
);